// The agency portfolio: which offices this session serves, and nothing more.
//
// ---------------------------------------------------------------------------
// There is no cross-office endpoint, and that is the design
// ---------------------------------------------------------------------------
//
// ch.06's agency screens are cross-office: "implement actual tenant selection
// rather than reusing one office's records". The obvious way to serve them
// would be an endpoint that reads several offices at once — and that endpoint
// would be the first thing in this system able to return two tenants' rows from
// one query, which is precisely the shape every isolation test in the repo
// exists to make impossible.
//
// So the portfolio is assembled the way GET /me is: an INDEX of the contexts
// this session may open, followed by one ordinary, verified, row-level-security
// filtered read per office. Nothing new is trusted. An office that is not in
// `me.memberships` is never requested; an office that IS requested is one
// app.begin_request has already confirmed a live membership for, or the request
// is a 404 identical to one for an office that does not exist.
//
// That is also acceptance V05 — "Office onboarding and agency portfolio
// summaries respect tenant grants and use the same source records as the office
// workspace" — held structurally rather than by care: the agency screens call
// the same endpoints the office screens call, with a different tenant in the
// path.
//
// ---------------------------------------------------------------------------
// "Granted" means a membership, and the role names which kind
// ---------------------------------------------------------------------------
//
// ch.02's role list puts the agency operator in an office rather than above it:
// they "configure connectors and record test/release evidence" for that office,
// and "agency support has no standing unrestricted invoice access. Implement
// expiring, tenant-specific support grants." A membership is per office and is
// written by the control plane alone — no office can grant it, and neither can
// an operator who already holds one.
//
// So the portfolio is exactly the offices where this user holds a LIVE
// `agency_operator` membership. Not every office they can see: somebody who
// owns their own office and operates three others has a portfolio of three, and
// their own office is a customer workspace rather than a line in it.
//
// Filtering here is presentation. Nothing about it is load-bearing: the API
// would serve any office this session is a member of, in any role, and
// including one by mistake would show a reviewer their own office under an
// agency heading rather than showing anybody another office's records.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api/client.ts';
import type { Me, Membership } from './api/types.ts';

/** ch.02's third role, as the server spells it. */
export const AGENCY_OPERATOR_ROLE = 'agency_operator';

/**
 * The offices this session serves as an agency operator, in the order GET /me
 * reported them.
 *
 * An empty array is an ordinary answer and the screens say so plainly: it means
 * this account operates no office, not that something failed.
 */
export function agencyMemberships(me: Me): readonly Membership[] {
  return me.memberships.filter((membership) => membership.role === AGENCY_OPERATOR_ROLE);
}

/**
 * What one office contributed to a portfolio read, or why it contributed
 * nothing.
 *
 * Per office rather than merged, deliberately. Four offices answering and one
 * failing is the ordinary case for a portfolio — a membership revoked a minute
 * ago, a request that timed out — and a screen that merged them could not tell
 * a reviewer which office it is not showing. A failure here is attached to the
 * office it happened to and to no other.
 */
export interface OfficeResult<T> {
  readonly membership: Membership;
  readonly value: T | null;
  /** Null when the read succeeded. Never both null and a null value. */
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// The fan-out: N offices, N verified reads, one result per office.
// ---------------------------------------------------------------------------

/**
 * Why a read of one office produced nothing, in words a reviewer can act on.
 *
 * A 404 here is not "there is nothing in that office". It is the answer the API
 * gives for an office this session is not a member of — which, on this screen,
 * means the membership went away between GET /me and this request. Saying so is
 * the honest version, and it is the reason failures are never merged into an
 * empty portfolio.
 */
export function describeOfficeFailure(error: unknown): string {
  if (error instanceof ApiError && error.isAbsent) {
    return 'No longer available to your session.';
  }
  if (error instanceof ApiError && error.code === 'transport_error') {
    return 'The service could not be reached.';
  }
  if (error instanceof ApiError && error.status === 403) {
    return 'This session may not read that here.';
  }
  return 'Could not be read.';
}

export interface OfficeReads<T> {
  /** Null while the current set of offices has not been read yet. */
  readonly results: readonly OfficeResult<T>[] | null;
  readonly busy: boolean;
  readonly reload: () => void;
}

/**
 * Read the same thing from every office in the portfolio, one at a time.
 *
 * Sequential rather than concurrent, on purpose: the API throttles per address
 * and a portfolio of twenty offices firing at once is the shape that trips it,
 * which would turn "this office could not be read" into the normal case. Slower
 * and complete beats fast and partly refused.
 *
 * When the set of offices changes, the previous results are dropped before the
 * new ones arrive rather than lingering under new headings — the browser's half
 * of the same rule the Administrations screen keeps when the office switcher
 * moves. `read` must be stable across renders; wrap it in useCallback.
 */
export function useOfficeReads<T>(
  offices: readonly Membership[],
  read: (membership: Membership) => Promise<T>,
): OfficeReads<T> {
  const key = offices.map((office) => office.tenantId).join(',');
  const latest = useRef(offices);
  latest.current = offices;

  const [state, setState] = useState<{
    key: string;
    results: readonly OfficeResult<T>[] | null;
  }>({ key: '\u0000never', results: null });
  const [busy, setBusy] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    void (async () => {
      const collected: OfficeResult<T>[] = [];
      for (const membership of latest.current) {
        try {
          collected.push({ membership, value: await read(membership), error: null });
        } catch (error) {
          // One office failing must not blank the others. A portfolio where
          // four answered and one did not is the ordinary case.
          collected.push({ membership, value: null, error: describeOfficeFailure(error) });
        }
      }
      if (cancelled) return;
      setState({ key, results: collected });
      setBusy(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [key, read, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  // Results from a DIFFERENT set of offices are not shown under this one.
  return { results: state.key === key ? state.results : null, busy, reload };
}
